import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Calendar, Clock, FileText, Mail, ChevronDown, CheckCircle, Send, Users, CalendarDays } from 'lucide-react';
import { getToken } from '../auth/authConfig';
import axios from 'axios';

interface APIEmployee {
  EmployeeName: string;
  Department: string;
  KT_Status: string;
  LastDay: string;
  employee_id: number;
}

interface Employee {
  id: string;
  name: string;
  department: string;
  lastDay: string;
  ktStatus: 'not-started' | 'in-progress' | 'completed';
  role: string;
  daysRemaining: number;
}

interface DashboardData {
  kt_completed: number;
  kt_in_progress: number;
  kt_not_started: number;
  total_empy_exit: number;
}

const ExitKTDetails: React.FC = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  
  const [selectedRole, setSelectedRole] = useState('');
  const [sessionSlots, setSessionSlots] = useState<string[]>([]);
  const [selectedStartDate, setSelectedStartDate] = useState('');
  const [selectedEndDate, setSelectedEndDate] = useState('');
  const [showRoleDetails, setShowRoleDetails] = useState(false);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingKT, setLoadingKT] = useState(false);

  const [empdata, setEmpData] = useState([]);

  const [employeeData, setEmployee] = useState({
    id: '',
    name: '',
    department: '',
    lastDay: ''
  });
  
  const [selectedRoleTitle, setSelectedRoleTitle] = useState<string>('');

  const employee1 = {
    id: employeeId,
    name: 'Sarah Johnson',
    department: 'Finance',
    lastDay: '2024-02-15'
  };

  const empData = empdata && empdata.filter(emp => emp.id === employeeId);
  const employee = empData.length > 0 ? empData[0] : employee1;
  console.log('Filtered employeeData Data:', employee);

  const roles = [
    {
      id: 'senior-analyst',
      title: 'Senior Financial Analyst',
      description: 'Responsible for financial analysis, reporting, and budget planning',
      keyResponsibilities: [
        'Monthly financial reporting and analysis',
        'Budget preparation and variance analysis',
        'Cash flow forecasting',
        'Financial modeling and projections'
      ]
    },
    {
      id: 'senior-full-Stack-developer',
      title: 'Senior Full Stack Developer',
      description: 'Responsible for financial analysis, reporting, and budget planning',
      keyResponsibilities: [
        'Monthly financial reporting and analysis',
        'Budget preparation and variance analysis',
        'Cash flow forecasting',
        'Financial modeling and projections'
      ]
    }
  ];

  const timeSlots = [
    '09:00 - 10:00',
    '10:00 - 11:00',
    '11:00 - 12:00',
    '14:00 - 15:00',
    '15:00 - 16:00',
    '16:00 - 17:00'
  ];

  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleRoleSelect = (roleId: string) => {
    setSelectedRole(roleId);
    const title = roles.find(role => role.id === roleId)?.title || '';
    setSelectedRoleTitle(title);
    setShowRoleDetails(true);
  };

  const handleStartDateChange = (date: string) => {
    setSelectedStartDate(date);
    if (selectedEndDate && date > selectedEndDate) {
      setSelectedEndDate('');
    }
    setSessionSlots([]);
  };

  const handleEndDateChange = (date: string) => {
    setSelectedEndDate(date);
    setSessionSlots([]);
  };

  const getDateRangeDays = () => {
    if (!selectedStartDate || !selectedEndDate) return 0;
    const start = new Date(selectedStartDate);
    const end = new Date(selectedEndDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  const handleSlotToggle = (slot: string) => {
    setSessionSlots(prev => 
      prev.includes(slot) 
        ? prev.filter(s => s !== slot)
        : [...prev, slot]
    );
  };

  const handleLaunchKT = async () => {
    try {
      const token = await getToken();
      setLoadingKT(true);
      
      const payload = {
        employee_id: Number(employee.id),
        EmployeeName: employee.name,
        RoleForKT: selectedRoleTitle || roles.find(r => r.id === selectedRole)?.title || '',
        KtSessionStartDate: selectedStartDate,
        KtSessionEndDate: selectedEndDate,
        KtSessionTime: sessionSlots[0],
        DocumentList: [],
        TaskID : employee.taskId,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_WEB_TOOL_API_BASE_URL}/start_KT`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'oidc-token': token
          }
        }
      );

      if (response.status === 200 || response.status === 201) {
        console.log('✅ KT Launched Successfully');
        setSuccess(true);
        setLoadingKT(false);
      } else {
        console.warn('⚠️ Unexpected response:', response.status);
        setLoadingKT(false);
      }
    } catch (error) {
      setLoadingKT(false);
      console.error('❌ Error launching KT process:', error);
    }
  };

  const handleBackToEmployees = () => {
    navigate('/');
  };

  const listOfEmployee = async () => {
    try {
      setLoading(true);
      const baseurl = `${process.env.REACT_APP_WEB_TOOL_API_BASE_URL}/employee_list`;
      const token = await getToken();

      const response = await axios.get(baseurl, {
        headers: { 'oidc-token': token }
      });

      if (response.status === 200) {
        const { data, dashboard }: { data: any, dashboard: any } = response.data;

        const transformed = data.map((emp: any) => ({
          id: emp.employee_id.toString(),
          name: emp.EmployeeName,
          department: emp.Department,
          lastDay: emp.LastDay,
          taskId: emp.task_id || '',
          role: 'Employee',
        }));

        setEmpData(transformed);
      }
    } catch (error) {
      console.error('Error fetching employee list:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    listOfEmployee();
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <div className="p-6">
      {!success && (
        <>
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 bg-blue-600 rounded-xl shadow-lg">
                <User className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Exit KT Details</h1>
                <p className="text-gray-600 mt-1">Initiate knowledge transfer process</p>
              </div>
            </div>

            {/* Employee Info Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Employee Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Employee Name</p>
                    <p className="font-medium text-gray-900">{employee.name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Department</p>
                    <p className="font-medium text-gray-900">{employee.department}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Last Working Day</p>
                    <p className="font-medium text-gray-900">{employee.lastDay}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Role Selection */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Select Role for KT</h2>
            <p className="text-gray-600 mb-6">Choose the role for which knowledge transfer will be conducted. Only one role can be selected at a time.</p>
            
            <div className="space-y-4">
              {roles.map((role) => (
                <div key={role.id} className="border border-gray-200 rounded-lg overflow-hidden">
                  <label className="flex items-center p-4 cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="role"
                      value={role.id}
                      checked={selectedRole === role.id}
                      onChange={() => handleRoleSelect(role.id)}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <div className="ml-4 flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-medium text-gray-900">{role.title}</h3>
                        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${selectedRole === role.id ? 'rotate-180' : ''}`} />
                      </div>
                      <p className="text-gray-600 mt-1">{role.description}</p>
                    </div>
                  </label>
                  
                  {selectedRole === role.id && showRoleDetails && (
                    <div className="px-4 pb-4 bg-blue-50 border-t border-blue-100">
                      <h4 className="font-medium text-gray-900 mb-2 py-4">Key Responsibilities:</h4>
                      <ul className="space-y-1">
                        {role.keyResponsibilities.map((responsibility, index) => (
                          <li key={index} className="text-sm text-gray-700 flex items-start gap-2">
                            <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                            {responsibility}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Session Cadence */}
          {selectedRole && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Confirm Session Cadence</h2>
              <p className="text-gray-600 mb-6">Select the date range and time slots for KT sessions. Multiple slots can be selected for each day.</p>
              
              {/* Date Range Selection */}
              <div className="mb-8">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Session Date Range</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Start Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Session Start Date
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        value={selectedStartDate}
                        onChange={(e) => handleStartDateChange(e.target.value)}
                        min={getTodayDate()}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      />
                      <CalendarDays className="absolute right-3 top-3 w-5 h-5 text-gray-400 pointer-events-none" />
                    </div>
                    {selectedStartDate && (
                      <p className="mt-2 text-sm text-blue-600 font-medium">
                        {formatDate(selectedStartDate)}
                      </p>
                    )}
                  </div>

                  {/* End Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Session End Date
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        value={selectedEndDate}
                        onChange={(e) => handleEndDateChange(e.target.value)}
                        min={selectedStartDate || getTodayDate()}
                        disabled={!selectedStartDate}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-50 disabled:text-gray-400"
                      />
                      <CalendarDays className="absolute right-3 top-3 w-5 h-5 text-gray-400 pointer-events-none" />
                    </div>
                    {selectedEndDate && (
                      <p className="mt-2 text-sm text-blue-600 font-medium">
                        {formatDate(selectedEndDate)}
                      </p>
                    )}
                  </div>
                </div>

                {/* Date Range Summary */}
                {selectedStartDate && selectedEndDate && (
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-5 h-5 text-blue-600" />
                      <h4 className="font-medium text-blue-900">Session Period Summary</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-blue-700 font-medium">Start:</span>
                        <p className="text-blue-800">{formatDate(selectedStartDate)}</p>
                      </div>
                      <div>
                        <span className="text-blue-700 font-medium">End:</span>
                        <p className="text-blue-800">{formatDate(selectedEndDate)}</p>
                      </div>
                      <div>
                        <span className="text-blue-700 font-medium">Duration:</span>
                        <p className="text-blue-800">{getDateRangeDays()} days</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Time Slots Selection */}
              {selectedStartDate && selectedEndDate && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Available Time Slots</h3>
                  <p className="text-gray-600 mb-4">Select 60-minute time slots for KT sessions. Multiple slots can be selected.</p>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {timeSlots.map((slot) => (
                      <label key={slot} className="flex items-center p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                        <input
                          type="checkbox"
                          checked={sessionSlots.includes(slot)}
                          onChange={() => handleSlotToggle(slot)}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <div className="ml-3 flex items-center gap-2">
                          <Clock className="w-4 h-4 text-gray-400" />
                          <span className="text-sm font-medium text-gray-900">{slot}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                  
                  {sessionSlots.length > 0 && (
                    <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <h4 className="font-medium text-green-900">Session Schedule Confirmed</h4>
                      </div>
                      <div className="text-sm text-green-800">
                        <p className="mb-2">
                          <strong>Period:</strong> {formatDate(selectedStartDate)} to {formatDate(selectedEndDate)} ({getDateRangeDays()} days)
                        </p>
                        <p>
                          <strong>Selected time slots:</strong> {sessionSlots.join(', ')}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Launch KT Button */}
          {selectedRole && selectedStartDate && selectedEndDate && sessionSlots.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-start gap-4 mb-6">
                <Mail className="w-6 h-6 text-blue-600 mt-1" />
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Ready to Launch KT Process</h3>
                  <p className="text-gray-600 text-sm">
                    Clicking "Launch KT" will trigger emails to the exiting employee and their manager with:
                  </p>
                  <ul className="mt-2 text-sm text-gray-600 space-y-1">
                    <li>• List of mandatory documents needed</li>
                    <li>• Link to upload required documents</li>
                    <li>• Selected session schedule and time slots</li>
                    <li>• KT session date range and duration</li>
                  </ul>
                </div>
              </div>
              
              <button
                onClick={handleLaunchKT}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 transition-colors shadow-sm"
                disabled={loadingKT}
              >
                {loadingKT ? 'Launching KT...' : 'Launch KT Process'}
              </button>
            </div>
          )}
        </>
      )}

      {success && (
        <div className="flex justify-center bg-gradient-to-br from-green-50 to-blue-50">
          <div className="max-w-4xl mx-auto p-8">
            {/* Success Animation Container */}
            <div className="text-center mb-8">
              <div className="relative mb-6">
                <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-scale-in">
                  <CheckCircle className="w-12 h-12 text-green-600" />
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center animate-fade-in">
                  <Send className="w-4 h-4 text-blue-600" />
                </div>
              </div>
              
              <h1 className="text-4xl font-bold text-gray-900 mb-4 animate-fade-in">
                KT Process Successfully Launched!
              </h1>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto animate-fade-in">
                The knowledge transfer process has been initiated and notification emails have been sent to all relevant stakeholders.
              </p>
            </div>

            {/* Success Details Cards */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* Employee Notification Card */}
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 animate-fade-in">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <User className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Employee Notified</h3>
                    <p className="text-gray-600 text-sm mb-3">
                      {employee.name} has received a comprehensive email with:
                    </p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                        KT session schedule details
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                        Required documents checklist
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                        Document upload portal access
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    <span>Email delivered successfully</span>
                  </div>
                </div>
              </div>

              {/* Manager Notification Card */}
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 animate-fade-in">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Manager Informed</h3>
                    <p className="text-gray-600 text-sm mb-3">
                      The reporting manager has been notified with:
                    </p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                        KT process timeline overview
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                        Progress tracking dashboard
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                        Role transition planning guide
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    <span>Email delivered successfully</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in">
              <button
                onClick={handleBackToEmployees}
                className="px-8 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors shadow-sm flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back 
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExitKTDetails;